/**
 * Thin wrapper over @capacitor/haptics.
 *
 * Dynamically imported so the web bundle doesn't hard-depend on the
 * native plugin, and every call is guarded — on the web build (or if the
 * plugin is unavailable) the Capacitor web shim falls back to
 * navigator.vibrate where supported, and otherwise these are silent
 * no-ops. A failing haptic must never break the quiz flow.
 */

export async function hapticImpact(
  style: "light" | "medium" | "heavy" = "medium"
): Promise<void> {
  try {
    const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
    const map = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    };
    await Haptics.impact({ style: map[style] });
  } catch {
    /* not available on this platform — ignore */
  }
}

export async function hapticNotify(
  type: "success" | "warning" | "error" = "success"
): Promise<void> {
  try {
    const { Haptics, NotificationType } = await import("@capacitor/haptics");
    const map = {
      success: NotificationType.Success,
      warning: NotificationType.Warning,
      error: NotificationType.Error,
    };
    await Haptics.notification({ type: map[type] });
  } catch {
    /* ignore */
  }
}

/** Correct answer — a crisp success buzz. */
export const hapticCorrect = () => hapticNotify("success");
/** Wrong answer — an error buzz. */
export const hapticWrong = () => hapticNotify("error");
/** Badge unlock / big moment — a heavier tap. */
export const hapticCelebrate = () => hapticImpact("heavy");
